/**
 * Whole-dashboard "Build with AI" panel (S9) — slide-over launched from the
 * toolbar (`DashboardToolbar`'s "Build with AI" button, mounted by
 * `DashboardTab`). Proposes MULTIPLE tiles from one prompt (`aiTileProposal`
 * + `useDashboardAiPropose`); each proposal card previews independently
 * (read-only, S3 policy) and can be rejected. "Add all" adds every still-
 * pending proposal (`useDashboardAiTileAdd`) — partial failures are per-
 * proposal, never silently dropped.
 */
import { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { ConnectionSelector } from '../../common/components/ConnectionSelector';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import type { Dashboard, DashboardEngineId } from '../types/dashboardTypes';
import { useDashboardAiPropose } from '../hooks/useDashboardAiPropose';
import { useDashboardAiTileAdd } from '../hooks/useDashboardAiTileAdd';
import { AiProposalCard } from './AiProposalCard';
import { safeConnectionAlias, safeConnectionName, safeConnectionServer } from '@/types/safeConnectionDisplay';

const KNOWN_ENGINES: ReadonlySet<string> = new Set(['mssql', 'pgsql', 'mysql', 'oracle', 'sqlite', 'kusto']);
const asEngine = (engine?: string): DashboardEngineId | undefined =>
  KNOWN_ENGINES.has(engine ?? '') ? (engine as DashboardEngineId) : undefined;

export function BuildWithAiPanel({
  dashboard,
  open,
  onOpenChange,
}: {
  dashboard: Dashboard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [connectionId, setConnectionId] = useState('');
  const [prompt, setPrompt] = useState('');
  const active = useConnectionStore((s) => s.activeConnections).find((c) => c.id === connectionId);
  const database = active?.connection.database;
  const { state, propose, reset } = useDashboardAiPropose();
  const { statuses, seed, reject, addOne, addAll } = useDashboardAiTileAdd(
    dashboard,
    connectionId,
    database,
    asEngine(active?.connection.engine),
  );

  useEffect(() => {
    if (!open || connectionId) return;
    const first = useConnectionStore.getState().activeConnections.find((c) => c.status === 'connected');
    if (first) setConnectionId(first.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only seed a default once per open
  }, [open]);

  useEffect(() => {
    if (state.status === 'done') seed(state.proposals);
  }, [state, seed]);

  const close = () => {
    onOpenChange(false);
    reset();
    setPrompt('');
  };

  const generate = () => {
    if (!connectionId || !prompt.trim()) return;
    void propose({
      connectionId,
      connectionAlias: safeConnectionName(active?.connection, connectionId),
      engine: active?.connection.engine ?? 'mssql',
      database,
      prompt: prompt.trim(),
      mode: 'multi',
    });
  };

  const proposals = state.status === 'done' ? state.proposals : [];
  const pending = proposals.filter((p) => {
    const status = statuses[p.id]?.status ?? 'pending';
    return status !== 'added' && status !== 'rejected';
  });

  return (
    <div
      className={`absolute inset-y-0 right-0 z-20 flex w-[390px] max-w-[92vw] flex-col border-l border-border bg-card shadow-lg transition-transform duration-200 ease-out motion-reduce:transition-none ${
        open ? 'translate-x-0' : 'translate-x-full'
      }`}
      data-testid="build-with-ai-panel"
      data-open={open}
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400" aria-hidden />
        <span className="text-sm font-semibold">Build with AI</span>
        <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
          read-only
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          data-testid="build-with-ai-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
        <div data-testid="build-with-ai-connection">
          <ConnectionSelector
            connectionId={connectionId || undefined}
            connectionName={active?.connection.alias ? safeConnectionAlias(active.connection) : undefined}
            serverHost={safeConnectionServer(active?.connection)}
            onConnectionChange={(id) => setConnectionId(id)}
            showAllEngines
          />
        </div>
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Add a section about returns: volume by day, top return reasons, and the return rate vs orders."
          rows={3}
          className="text-xs"
          disabled={state.status === 'running'}
          data-testid="build-with-ai-prompt"
        />
        <Button
          type="button"
          size="sm"
          className="h-7 w-fit gap-1.5"
          onClick={generate}
          disabled={state.status === 'running' || !connectionId || !prompt.trim()}
          title={
            !connectionId ? 'Pick a connection first' : !prompt.trim() ? 'Describe what you want first' : undefined
          }
          data-testid="build-with-ai-generate"
        >
          {state.status === 'running' ? <RandomDotmSpinner size={14} /> : null}
          Generate tiles
        </Button>

        {state.status === 'running' && (
          <div className="flex flex-col gap-1 text-xs text-muted-foreground" data-testid="build-with-ai-progress">
            <span>{state.step === 'schema' ? 'Inspecting schema…' : 'Inspecting schema ✓'}</span>
            {state.step === 'drafting' && <span>Drafting queries — read-only, preview before run</span>}
          </div>
        )}

        {state.status === 'error' && (
          <div className="flex flex-col gap-1.5 text-xs text-destructive" data-testid="build-with-ai-error">
            <span>{state.message}</span>
            <Button type="button" size="sm" variant="outline" className="h-6 w-fit text-xs" onClick={reset}>
              Try again
            </Button>
          </div>
        )}

        {proposals.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="build-with-ai-proposals">
            {proposals.map((p) => (
              <AiProposalCard
                key={p.id}
                proposal={p}
                status={statuses[p.id]?.status ?? 'pending'}
                errorMessage={statuses[p.id]?.error}
                connectionId={connectionId}
                database={database}
                dashboardId={dashboard.id}
                onReject={() => reject(p.id)}
                onAdd={() => void addOne(p)}
              />
            ))}
          </div>
        )}

        {pending.length > 0 && (
          <Button
            type="button"
            size="sm"
            className="h-7 w-fit"
            onClick={() => void addAll(pending)}
            data-testid="build-with-ai-add-all"
          >
            Add all {pending.length} tile{pending.length === 1 ? '' : 's'}
          </Button>
        )}
      </div>
    </div>
  );
}
